// React hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers i API servisi
import { useTranslate } from "helpers";
import { deleteFleetVehicleExpenseAPI, getFleetVehicleExpenseStatsAPI, getFleetVehicleExpensesAPI } from "services/api/fleet/vehicles/expenses/api";

import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { FleetVehicleExpenseRow, FleetVehicleExpenseSearchFormValues } from "services/api/fleet/vehicles/expenses/types";
import { initialFleetVehicleExpenseSearchForm } from "services/api/fleet/vehicles/expenses/consts";
import {
  VehicleExpenseSearchPageCmp,
  VehicleExpenseSearchPageProps,
} from "./types";
import { FLEET_VEHICLE_EXPENSE_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import TabMenu from "ui/modules/fleet/vehicles/pages/components/TabMenu/TabMenu";
import { VehicleTabHeader } from "ui/modules/fleet/vehicles/pages/components/VehicleTabHeader";

// Lokalne komponente
import VehicleExpenseSearch from "./components/VehicleExpenseSearch/VehicleExpenseSearch";
import VehicleExpenseStats from "./components/VehicleExpenseStats/VehicleExpenseStats";
import VehicleExpenseEditCard from "../VehicleExpenseEditCard/VehicleExpenseEditCard";
import { Dialog } from "ui/components/Dialog";
import Tabs from "ui/components/Tabs/Tabs";
import { FleetVehicleDetailFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";

const VehicleExpenseSearchPage: VehicleExpenseSearchPageCmp = (
  props: VehicleExpenseSearchPageProps
) => {
  const { id } = useParams();
  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetVehicleDetailFormValues>(
    initialFleetVehicleDetailForm
  );
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleExpenseRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [expenseId, setExpenseId] = useState<string>("");
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<FleetVehicleExpenseSearchFormValues>(initialFleetVehicleExpenseSearchForm);

  // Stabilan referenca za VehicleExpenseStats — useCallback drži je istom kroz
  // re-rendere, inače bi useEffect u Monthly/Weekly refetchao na svaki render.
  const fetchStats = useCallback(() => getFleetVehicleExpenseStatsAPI(id!), [id]);

  useEffect(() => {
    if (id) {
      fetchVehicle(id);
      fetchSearchResult(id, searchResults.pagination, filters);
    }
  }, [id]);

  const fetchVehicle = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);
  };

  const fetchSearchResult = async (
    vehicleId: string,
    pagination: Pagination,
    searchFilters: FleetVehicleExpenseSearchFormValues
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleExpensesAPI(pagination, {
        vehicleId,
        search: searchFilters.search,
        expenseTypeId: searchFilters.expenseTypeId,
        expenseDateFrom: searchFilters.expenseDateFrom,
        expenseDateTo: searchFilters.expenseDateTo,
        amountMin: searchFilters.amountMin,
        amountMax: searchFilters.amountMax,
      });

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleSearchChange = useCallback((formValues: FleetVehicleExpenseSearchFormValues) => {
    setFilters(formValues);
    if (id) fetchSearchResult(id, searchResults.pagination, formValues);
  }, [id, searchResults.pagination]);

  const handleRowClick = useCallback(async (id: string) => {
    setExpenseId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setExpenseId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    if (id) fetchSearchResult(id, searchResults.pagination, filters);

    setExpenseId("");
    setShowAddNewModal(false);
  }, [id, searchResults.pagination, filters]);


  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id) fetchSearchResult(id, newPagination, filters);
  };


  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setExpenseId(id);
    setOpenDialog(true);
  }

  const handleExpenseDeleteDialogConfirm = useCallback(async () => {
    if (!expenseId) return;

    try {
      const deleted = await deleteFleetVehicleExpenseAPI(expenseId, t);

      if (deleted && id) {
        await fetchSearchResult(id, searchResults.pagination, filters);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setExpenseId("");
    } catch (err) {
      console.error("Greška prilikom brisanja troška:", err);
    }
  }, [expenseId, t, id, searchResults.pagination, filters]);

  const handleExpenseDeleteDialogClose = useCallback(() => {
    setExpenseId("");
    setOpenDialog(false);
  }, []);



  // Promjena statusa preko značke — vozilo se dohvaća ponovno da se značka osvježi.
  const handleStatusChange = useCallback(() => {
    if (id) fetchVehicle(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <VehicleTabHeader
            vehicleId={id}
            vehicle={formValues}
            title={t(FLEET_VEHICLE_EXPENSE_SEARCH_PAGE_TITLE.text)}
            icon={FLEET_VEHICLE_EXPENSE_SEARCH_PAGE_TITLE.icon}
            iconColor={FLEET_VEHICLE_EXPENSE_SEARCH_PAGE_TITLE.iconColor}
            onStatusChange={handleStatusChange}
          />
        </div>
        <TabMenu vehicleId={id} />
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <Tabs
          
            queryParamName="view"
            tabs={[
              {
                label: "Pretraga",
                queryParam: "search",
                content: (
                  <VehicleExpenseSearch
                    isLoading={isLoading}
                    searchResults={searchResults}
                    selectedRows={selectedRows}
                    onAddNew={handleAddNew}
                    onSearchChange={handleSearchChange}
                    onRowClick={handleRowClick}
                    onPaginationChange={handlePaginationChange}
                    onSelectedChange={handleSelectedChange}
                    onDeleteClick={handleDeleteClick}
                  />
                ),
              },
              {
                label: "Statistika",
                queryParam: "stats",
                content: <VehicleExpenseStats fetchStats={fetchStats} />,
              },
            ]}
          />
        </div>
      </div>
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.expense.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.expense.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.expense.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.expense.dialog.delete.canceltext"
        )}
        onConfirm={handleExpenseDeleteDialogConfirm}
        onCancel={handleExpenseDeleteDialogClose}
      />
      <VehicleExpenseEditCard
        show={showAddNewModal}
        vehicleId={id}
        expenseId={expenseId}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
    </>
  );
};

VehicleExpenseSearchPage.route =
  FleetVehicleRoutes.FleetVehicleExpenseSearchPageRouter;

export default VehicleExpenseSearchPage;
